import { useCallback } from 'react';

import type { RequestedAttachmentPayload } from '#/api/ats';
import type { ApiOrgId, ApiVolopId } from '#/api/types/ApiTypedId';
import { Form } from '#/components/Form/Form';
import type { FormSubmitFunction } from '#/components/Form/FormContext';
import { GeneralError } from '#/components/Form/GeneralError';
import { InputField } from '#/components/Form/InputField';
import { SubmitButton } from '#/components/Form/SubmitButton/SubmitButton';
import { useI18n } from '#/i18n/hooks/useI18n';
import { embedSearchWidgetApiCreateListingApplication } from '#/modules/embedSearchWidget/api/listingApplication/embedSearchWidgetApiCreateListingApplication';
import type { EmbedSearchWidgetApiMetadata } from '#/modules/embedSearchWidget/api/types/EmbedSearchWidgetApiMetadata';
import { EmbedSearchWidgetRequestedAttachmentUserField } from '#/modules/embedSearchWidget/fields/EmbedSearchWidgetRequestedAttachmentUserField';
import { trackAppliedToListing } from '#/modules/listing/analytics/trackAppliedToListing';
import { ListingRequestedAttachmentsFieldSwitch } from '#/modules/listing/components/ApplicationFields/ListingRequestedAttachmentsFieldSwitch';
import { ListingViewAppliedMessage } from '#/modules/listing/components/ListingView/AppliedMessage/ListingViewAppliedMessage';
import type { ApiOrgType } from '#/modules/listing/orgs/api/types/ApiOrgType';
import { gutterWidth } from '#/theme/theme';

type EmbedSearchWidgetVolopApplyValues = {
  firstName: string;
  lastName: string;
  email: string;
  requestedAttachments: (RequestedAttachmentPayload | null)[];
  requestedAttachmentsHash: string;
};

type Props = {
  embedSearchWidget: EmbedSearchWidgetApiMetadata;
  volopId: ApiVolopId;
  applied: boolean;
  onApplied: () => void;
  orgId: ApiOrgId | null;
  orgType: ApiOrgType | null;
  requestedAttachmentsHash: string;
};

export function EmbedSearchWidgetViewVolopPageSimpleApply({
  embedSearchWidget,
  volopId,
  applied,
  onApplied,
  orgId,
  orgType,
  requestedAttachmentsHash,
}: Props) {
  const { i18n } = useI18n();

  const allFieldsRequired =
    embedSearchWidget.requestedAttachments.filter((ra) => !ra.isRequired)
      .length === 0;

  const onSubmit: FormSubmitFunction<EmbedSearchWidgetVolopApplyValues> =
    useCallback(
      async ({ values, handleError }) => {
        try {
          await embedSearchWidgetApiCreateListingApplication({
            listingId: volopId,
            listingType: 'VOLOP',
            searchWidgetId: embedSearchWidget.id,
            email: values.email,
            firstName: values.firstName,
            lastName: values.lastName,
            resumeS3FileId: null,
            requestedAttachments: values.requestedAttachments,
            requestedAttachmentsHash,
          });

          onApplied();
        } catch (error) {
          handleError(error as Error);
        }

        trackAppliedToListing({
          listingId: volopId,
          listingType: 'VOLOP',
          orgId,
          orgType,
          isNewResume: true,
          hasAts: false,
        });
      },
      [
        embedSearchWidget.id,
        onApplied,
        orgId,
        orgType,
        volopId,
        requestedAttachmentsHash,
      ],
    );

  if (applied) return <ListingViewAppliedMessage />;

  return (
    <div style={{ marginTop: 48 }}>
      <Form
        analyticsTitle="[embed] Volop page - how to apply section - apply"
        usageContext="page"
        qaId="listing-application-form"
        initialValues={
          {
            firstName: '',
            lastName: '',
            email: '',
            requestedAttachments: Array(
              embedSearchWidget.requestedAttachments.length,
            ).fill(null) as null[],
            requestedAttachmentsHash,
          } satisfies EmbedSearchWidgetVolopApplyValues
        }
        onSubmit={onSubmit}
      >
        <GeneralError />

        <InputField
          name="firstName"
          label={`${i18n('First Name')}*`}
          placeholder={i18n('Casey')}
        />

        <InputField
          name="lastName"
          label={`${i18n('Last Name')}*`}
          placeholder={i18n('Smith')}
        />

        <InputField
          name="email"
          label={`${i18n('Email')}*`}
          placeholder={i18n('casey.smith@example.com')}
        />

        {embedSearchWidget.requestedAttachments.map(
          (requestedAttachment, index) => (
            <ListingRequestedAttachmentsFieldSwitch
              // oxlint-disable-next-line no-array-index-key
              key={index}
              requestedAttachment={requestedAttachment}
              index={index}
              allFieldsRequired={allFieldsRequired}
              disabled={false}
              renderRequestedAttachmentFileField={({
                key,
                name,
                label,
                disabled,
                requestedFileAttachment,
              }) => (
                <EmbedSearchWidgetRequestedAttachmentUserField
                  key={key}
                  name={name}
                  label={label}
                  disabled={disabled}
                  analyticsLabel={requestedFileAttachment.label}
                  downloadAttachment={false}
                  fileType="LISTING_APPLICATION"
                  listingType="VOLOP"
                />
              )}
            />
          ),
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: gutterWidth,
          }}
        >
          <SubmitButton>
            {embedSearchWidget.buttonText ?? i18n('Submit Your Form')}
          </SubmitButton>
        </div>
      </Form>
    </div>
  );
}
